import { Box, useMediaQuery, useTheme, Snackbar, Alert } from "@mui/material";
import { operationsDashboardStyles } from "../OperationsDashboard/styles";
import FilterComponent from "../OperationsDashboard/FilterComponent";
import {
  FilterComponentRef,
  OperationsAppliedFilter,
  SimpleFilterState,
  OperationsDashboardProps,
} from "../OperationsDashboard/type";
import ActiveFilterSection from "../ActiveFiterSection";
import React, { useMemo } from "react";
import { Content } from "./Components/Content";
import { FilterModal } from "../OperationsDashboard/components/FilterModal";
import { KeyboardTab } from "@mui/icons-material";
import { useOperationsDashboardData } from "../OperationsDashboard/useOperationsDashboardData";
import useFetch from "../../hooks/useFetch";
import { baseUrl } from "../../config";
import dayjs from "dayjs";
import Loading from "../Loading";
import { getDisplayLabelForSelectedUnit } from "../OperationsDashboard/components/UnitHierarchyFilter/serviceLineTreeUtils";
import { revenueAndBilledHoursStyles } from "./Styles/styles";
import axiosConfig from "../../utils/axiosConfig";
import type {
  HierarchyTreeNode,
  HierarchyTableTotal,
  HierarchyLevel,
} from "./Types/types";

const EMPTY_VALUE = "___EMPTY___";

const transformMetrics = (metrics: any) => {
  return {
    revenue_cm1: metrics?.revenue_cm1 ?? 0,
    revenue_period: metrics?.revenue_period ?? 0,
    gm_percent: metrics?.gm_percent ?? 0,
    billed_hours_cm1: {
      offshore: metrics?.offshore_billed_hours_cm1 ?? 0,
      onsite: metrics?.onsite_billed_hours_cm1 ?? 0,
      total: metrics?.total_billed_hours_cm1 ?? 0,
    },
    billed_hours_period: {
      offshore: metrics?.offshore_billed_hours ?? 0,
      onsite: metrics?.onsite_billed_hours ?? 0,
      total: metrics?.total_billed_hours ?? 0,
    },
  };
};

const transformNode = (node: any): HierarchyTreeNode => {
  return {
    entity_id: String(node.entity_id),
    label: node.label ?? "",
    level: node.level,
    metrics: transformMetrics(node.metrics),
    is_expandable: node.is_expandable ?? false,
    children: node.children
      ? node.children.map(transformNode)
      : node.is_expandable
        ? []
        : undefined,
  };
};

const updateNodeChildren = (
  nodes: HierarchyTreeNode[],
  targetId: string,
  children: HierarchyTreeNode[],
): HierarchyTreeNode[] => {
  return nodes.map((node) => {
    if (node.entity_id === targetId) {
      return {
        ...node,
        children: children,
      };
    } else if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateNodeChildren(node.children, targetId, children),
      };
    }
    return node;
  });
};

export const RevenueAndBilledHours = ({
  variant = "overview",
}: OperationsDashboardProps) => {
  const getVariantDefaultDateRange = React.useCallback(() => {
    const endDate = dayjs().endOf("month");
    const startDate = dayjs().subtract(5, "month").startOf("month");
    return {
      startDate,
      endDate,
      label: `${startDate.format("MMM YYYY")} - ${endDate.format("MMM YYYY")}`,
    };
  }, [variant]);

  const filterRef = React.useRef<FilterComponentRef>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    businessUnits,
    businessUnitFilterTree,
    serviceLineOptions,
    serviceLineFilterTree,
    fetchServiceLineOptions,
    loading,
    fetchBusinessUnits,
    verticalOptions,
    fetchVerticalOptions,
    clearVerticalOptions,
    customerGroupOptions,
    fetchCustomerGroupOptions,
    clearCustomerGroupOptions,
    customerOptions,
    fetchCustomerOptions,
    clearCustomerOptions,
    prefillValues,
  } = useOperationsDashboardData();

  const getDefaultFilters = (): SimpleFilterState => {
    const { startDate, endDate, label } = getVariantDefaultDateRange();

    const result = {
      accounts:
        prefillValues.customerGroup?.value !== undefined &&
        customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [],
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
      verticals:
        prefillValues.vertical?.value && prefillValues.vertical.value !== ""
          ? prefillValues.vertical.value
          : "",
      businessUnit:
        prefillValues.businessUnit?.value &&
        prefillValues.businessUnit.value !== ""
          ? prefillValues.businessUnit.value
          : "",
      serviceLine:
        prefillValues.serviceLine?.value &&
        prefillValues.serviceLine.value !== ""
          ? prefillValues.serviceLine.value
          : "",
      dateStart: startDate,
      dateEnd: endDate,
      dateLabel: label,
    };
    return result;
  };

  const getEmptyFilters = (): SimpleFilterState => {
    const { startDate, endDate, label } = getVariantDefaultDateRange();

    return {
      accounts: [],
      customers: [],
      verticals: "",
      businessUnit: "",
      serviceLine: "",
      dateStart: startDate,
      dateEnd: endDate,
      dateLabel: label,
    };
  };

  const [currentFilters, setCurrentFilters] =
    React.useState<SimpleFilterState>(getEmptyFilters());

  const setCurrentFiltersWithDebug = React.useCallback((newFilters: any) => {
    if (typeof newFilters === "function") {
      setCurrentFilters((prev: SimpleFilterState) => {
        const result = newFilters(prev);
        return result;
      });
    } else {
      setCurrentFilters(newFilters);
    }
  }, []);

  const [appliedFilters, setAppliedFilters] =
    React.useState<SimpleFilterState>(getEmptyFilters());

  const [shouldApplyfilters, setShouldApplyfilters] = React.useState(false);

  const prefillApplied = React.useRef(false);
  const userManualChanges = React.useRef<Set<string>>(new Set());

  const [openErrorSnackbar, setOpenErrorSnackbar] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const isPrimaryLoadingComplete = () => {
    return (
      !loading.businessUnits &&
      !loading.serviceLineOptions &&
      !loading.verticals
    );
  };

  const hasPrefillValues = () => {
    return (
      Object.keys(prefillValues).length > 0 &&
      Object.values(prefillValues).some((value) => value?.value !== undefined)
    );
  };

  const [isFilterModalOpen, setIsFilterModalOpen] = React.useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = React.useState(true);
  const [costType, setCostType] = React.useState("actual");

  const queryParams = useMemo(() => {
    const startDate = appliedFilters.dateStart
      ? dayjs(appliedFilters.dateStart)
      : dayjs().startOf("year");
    const endDate = appliedFilters.dateEnd
      ? dayjs(appliedFilters.dateEnd)
      : dayjs().endOf("year");

    return {
      start_year: startDate.year(),
      start_month: startDate.month() + 1,
      end_year: endDate.year(),
      end_month: endDate.month() + 1,
      business_unit: appliedFilters.businessUnit ?? "",
      service_line: appliedFilters.serviceLine ?? "",
      vertical: appliedFilters.verticals ?? "",
      customer: appliedFilters.customers
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      customer_group: appliedFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      cost_type: costType,
    };
  }, [appliedFilters, costType]);

  const overviewRequest = useMemo(() => {
    if (variant !== "overview") return null;
    return {
      method: "get" as const,
      url: `${baseUrl}/revenue-billed-hours`,
      params: queryParams,
    };
  }, [variant, queryParams]);

  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
  } = useFetch<any>(overviewRequest);

  const [treeData, setTreeData] = React.useState<HierarchyTreeNode[]>([]);
  const [treeTotal, setTreeTotal] = React.useState<
    HierarchyTableTotal | undefined
  >(undefined);
  const [loadingNodes, setLoadingNodes] = React.useState<Set<string>>(
    new Set(),
  );

  const transformedRoot = React.useMemo(() => {
    if (!dashboardData?.data) return null;
    return transformNode(dashboardData.data);
  }, [dashboardData]);

  const transformedTotal = React.useMemo(() => {
    if (!dashboardData?.data?.total) return undefined;
    return {
      label: "Total",
      metrics: transformMetrics(dashboardData.data.total),
    };
  }, [dashboardData]);

  React.useEffect(() => {
    if (transformedRoot) {
      setTreeData([transformedRoot]);
    } else {
      setTreeData([]);
    }
  }, [transformedRoot]);

  React.useEffect(() => {
    setTreeTotal(transformedTotal);
  }, [transformedTotal]);

  const handleExpandNode = React.useCallback(
    async (entityId: string, level: HierarchyLevel) => {
      if (loadingNodes.has(entityId)) return;

      const findNode = (
        nodes: HierarchyTreeNode[],
      ): HierarchyTreeNode | null => {
        for (const node of nodes) {
          if (node.entity_id === entityId) return node;
          if (node.children) {
            const found = findNode(node.children);
            if (found) return found;
          }
        }
        return null;
      };

      const node = findNode(treeData);
      if (node?.children && node?.children.length > 0) {
        return;
      }

      setLoadingNodes((prev) => {
        const next = new Set(prev);
        next.add(entityId);
        return next;
      });

      try {
        const response = await axiosConfig.get(
          `${baseUrl}/revenue-billed-hours`,
          {
            params: {
              ...queryParams,
              parent_id: entityId,
              level: level,
            },
          },
        );

        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const childNodes = response.data.data.map(transformNode);
          setTreeData((prevTree) =>
            updateNodeChildren(prevTree, entityId, childNodes),
          );
        } else {
          throw new Error(response.data?.message ?? "Invalid API response");
        }
      } catch (err: any) {
        console.error("Failed to fetch children for node:", entityId, err);
        setErrorMessage(err.message ?? "Failed to fetch drill-down data");
        setOpenErrorSnackbar(true);
      } finally {
        setLoadingNodes((prev) => {
          const next = new Set(prev);
          next.delete(entityId);
          return next;
        });
      }
    },
    [treeData, queryParams, loadingNodes],
  );

  React.useEffect(() => {
    const err = dashboardError;
    if (err) {
      setErrorMessage(
        err.message ?? "An error occurred while loading the dashboard data",
      );
      setOpenErrorSnackbar(true);
    }
  }, [dashboardError]);

  const handleCloseErrorSnackbar = () => {
    setOpenErrorSnackbar(false);
  };

  const handleMultiselectAllLogic = (
    values: string[],
    previousValues: string[],
  ): string[] => {
    if (values.length <= 1) {
      return values;
    }

    const hasAll = values.includes(EMPTY_VALUE);
    const hasSpecific = values.some((val) => val !== EMPTY_VALUE);
    const previousHasAll = previousValues.includes(EMPTY_VALUE);

    if (hasAll && hasSpecific) {
      if (!previousHasAll) {
        return [EMPTY_VALUE];
      } else {
        return values.filter((val) => val !== EMPTY_VALUE);
      }
    }

    return values;
  };

  const getPrefillValue = (
    prefillValue: string | undefined,
    hasManualChange: boolean,
    optionsLength: number,
  ): string[] => {
    if (prefillValue === undefined) {
      return [];
    }

    if (hasManualChange && optionsLength > 0) {
      return [prefillValue];
    }

    if (!hasManualChange) {
      return [prefillValue];
    }

    return [];
  };

  const handleAccountsChange = (values: string[]) => {
    const filteredValues = handleMultiselectAllLogic(
      values,
      currentFilters.accounts,
    );
    userManualChanges.current.add("accounts");

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => {
      return { ...prev, accounts: filteredValues };
    });
    setShouldApplyfilters(true);
  };

  React.useEffect(() => {
    if (
      customerGroupOptions.length === 0 &&
      currentFilters.accounts.length > 0
    ) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        accounts: [],
      }));
    }
  }, [customerGroupOptions.length, currentFilters.accounts.length]);

  React.useEffect(() => {
    if (customerOptions.length === 0 && currentFilters.customers.length > 0) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        customers: [],
      }));
    }
  }, [customerOptions.length, currentFilters.customers.length]);

  const handleCustomersChange = (values: string[]) => {
    const filteredValues = handleMultiselectAllLogic(
      values,
      currentFilters.customers,
    );

    userManualChanges.current.add("customers");

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      customers: filteredValues,
    }));
    setShouldApplyfilters(true);
  };

  const handleVerticalsChange = (value: string) => {
    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      verticals: value,
    }));
    userManualChanges.current.add("verticals");
    setShouldApplyfilters(true);
  };

  const appliedFilterLabelsCache = React.useRef<{
    customers: string[];
    accounts: string[];
    businessUnit: string;
    serviceLine: string;
    vertical: string;
  }>({
    customers: [],
    accounts: [],
    businessUnit: "",
    serviceLine: "",
    vertical: "",
  });

  const handleBusinessUnitChange = (value: string) => {
    if (value !== prefillValues.businessUnit?.value) {
      userManualChanges.current.add("businessUnit");
    }

    const hasBusinessUnitManualChange =
      userManualChanges.current.has("businessUnit");

    const accountsValue = getPrefillValue(
      prefillValues.customerGroup?.value,
      hasBusinessUnitManualChange,
      customerGroupOptions.length,
    );

    const customersValue = getPrefillValue(
      prefillValues.customer?.value,
      hasBusinessUnitManualChange,
      customerOptions.length,
    );

    const verticalsValue = hasBusinessUnitManualChange
      ? ""
      : (prefillValues.vertical?.value ?? "");

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      businessUnit: value,
      accounts: accountsValue,
      customers: customersValue,
      verticals: verticalsValue,
    }));
    setShouldApplyfilters(true);

    clearVerticalOptions();
  };

  const handleServiceLineChange = (value: string) => {
    if (value !== prefillValues.serviceLine?.value) {
      userManualChanges.current.add("serviceLine");
    }

    const hasServiceLineManualChange =
      userManualChanges.current.has("serviceLine");

    const getAccountsValue = () => {
      if (hasServiceLineManualChange) {
        return prefillValues.customerGroup?.value !== undefined &&
          customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [];
      }
      return prefillValues.customerGroup?.value !== undefined
        ? [prefillValues.customerGroup.value]
        : [];
    };

    const getCustomersValue = () => {
      if (hasServiceLineManualChange) {
        return prefillValues.customer?.value !== undefined &&
          customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [];
      }
      return prefillValues.customer?.value !== undefined
        ? [prefillValues.customer.value]
        : [];
    };

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      serviceLine: value,
      accounts: getAccountsValue(),
      customers: getCustomersValue(),
    }));
    setShouldApplyfilters(true);
  };

  const handleDateRangeChange = (start: any, end: any, label?: string) => {
    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      dateStart: start,
      dateEnd: end,
      dateLabel: label,
    }));
    setShouldApplyfilters(true);
  };

  const handleCostTypeChange = (newCostType: string) => {
    setCostType(newCostType);
  };

  const reFetchCustomerOptions = () => {
    fetchCustomerOptions({
      business_unit_id: prefillValues?.businessUnit?.value ?? "",
      service_line_id: prefillValues?.serviceLine?.value ?? "",
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
    });
    fetchCustomerGroupOptions({
      business_unit_id: prefillValues?.businessUnit?.value ?? "",
      service_line_id: prefillValues?.serviceLine?.value ?? "",
    });
  };

  const populateLabelCache = (filters: SimpleFilterState) => {
    const getMultiSelectLabels = (
      options: { label: string; value: string }[],
      values: string[],
    ): string[] => {
      if (!values || values.length === 0) return [];

      if (values.length === 1 && values[0] === EMPTY_VALUE) {
        return ["All"];
      }

      if (!options || options.length === 0) return [];

      const filteredValues = values.filter((v) => v !== EMPTY_VALUE);
      return mapLabels(options, filteredValues);
    };

    const customerLabels = getMultiSelectLabels(
      customerOptions,
      filters.customers,
    );
    const accountLabels = getMultiSelectLabels(
      customerGroupOptions,
      filters.accounts,
    );

    const serviceLineLabel =
      getLabel(serviceLineOptions, filters.serviceLine) ??
      (serviceLineFilterTree?.length
        ? getDisplayLabelForSelectedUnit(
            serviceLineFilterTree,
            filters.serviceLine,
          )
        : undefined) ??
      "";

    const businessUnitLabel =
      getLabel(businessUnits, filters.businessUnit) ??
      (businessUnitFilterTree?.length
        ? getDisplayLabelForSelectedUnit(
            businessUnitFilterTree,
            filters.businessUnit,
          )
        : undefined) ??
      "";

    appliedFilterLabelsCache.current = {
      customers: customerLabels,
      accounts: accountLabels,
      businessUnit: businessUnitLabel,
      serviceLine: serviceLineLabel,
      vertical: getLabel(verticalOptions, filters.verticals) ?? "",
    };
  };

  const arrayFilters: Array<{
    key: keyof SimpleFilterState;
    prefillKey: keyof typeof prefillValues;
  }> = [
    { key: "accounts", prefillKey: "customerGroup" },
    { key: "customers", prefillKey: "customer" },
  ];

  const stringFilters: Array<{
    key: keyof SimpleFilterState;
    prefillKey: keyof typeof prefillValues;
    emptyValue?: string;
  }> = [
    { key: "verticals", prefillKey: "vertical" },
    { key: "businessUnit", prefillKey: "businessUnit" },
    { key: "serviceLine", prefillKey: "serviceLine" },
  ];

  const handleApplyFilters = () => {
    const finalFilters = { ...currentFilters };

    const bothBuSlCleared =
      !finalFilters.businessUnit && !finalFilters.serviceLine;
    const bothBuSlHavePrefill =
      prefillValues.businessUnit?.value !== undefined &&
      prefillValues.serviceLine?.value !== undefined;

    if (bothBuSlCleared && bothBuSlHavePrefill) {
      finalFilters.businessUnit = prefillValues.businessUnit?.value ?? "";
      finalFilters.serviceLine = prefillValues.serviceLine?.value ?? "";
    } else if (bothBuSlCleared) {
      if (prefillValues.businessUnit?.value !== undefined) {
        finalFilters.businessUnit = prefillValues.businessUnit.value;
      }
      if (prefillValues.serviceLine?.value !== undefined) {
        finalFilters.serviceLine = prefillValues.serviceLine.value;
      }
    }

    arrayFilters.forEach(({ key, prefillKey }) => {
      const filterValue = finalFilters[key] as string[];
      if (
        (!filterValue || filterValue.length === 0) &&
        prefillValues[prefillKey]?.value === EMPTY_VALUE &&
        !userManualChanges.current.has("businessUnit") &&
        !userManualChanges.current.has("serviceLine")
      ) {
        finalFilters[key] = [EMPTY_VALUE];
      }
    });

    stringFilters.forEach(({ key, prefillKey, emptyValue }) => {
      if (
        bothBuSlCleared &&
        bothBuSlHavePrefill &&
        (key === "businessUnit" || key === "serviceLine")
      ) {
        return;
      }

      const filterValue = finalFilters[key] as string;
      const isEmpty = emptyValue ? filterValue === emptyValue : !filterValue;
      if (isEmpty && prefillValues[prefillKey]?.value === "") {
        finalFilters[key] = "";
      }
      if (
        isEmpty &&
        prefillValues[prefillKey]?.value === EMPTY_VALUE &&
        !userManualChanges.current.has("businessUnit") &&
        !userManualChanges.current.has("serviceLine")
      ) {
        finalFilters[key] = EMPTY_VALUE;
      }
    });

    populateLabelCache(finalFilters);
    setCurrentFiltersWithDebug(finalFilters);
    setAppliedFilters(finalFilters);
    setShouldApplyfilters(false);
    setIsFilterModalOpen(false);
  };

  const handleClearAllFilters = () => {
    filterRef.current?.clearAll();
    const defaultFilters = getDefaultFilters();
    setCurrentFiltersWithDebug(defaultFilters);
    setAppliedFilters(defaultFilters);
    setShouldApplyfilters(false);
    prefillApplied.current = false;
    userManualChanges.current.clear();
    appliedFilterLabelsCache.current = {
      customers: [],
      accounts: [],
      businessUnit: "",
      serviceLine: "",
      vertical: "",
    };
    fetchBusinessUnits();
    fetchServiceLineOptions();

    if (prefillValues.businessUnit?.value) {
      fetchVerticalOptions(prefillValues.businessUnit.value);
    }
  };

  const getLabel = (opts: { label: string; value: string }[], value?: string) =>
    opts.find((o) => o.value === value)?.label;

  const mapLabels = (
    opts: { label: string; value: string }[],
    values: string[],
  ) =>
    values
      .map((v) => opts.find((o) => o.value === v)?.label)
      .filter(Boolean) as string[];

  const appliedFilter = React.useMemo((): OperationsAppliedFilter => {
    const hasBuOrSlValue =
      (appliedFilters.businessUnit && appliedFilters.businessUnit !== "") ||
      (appliedFilters.serviceLine && appliedFilters.serviceLine !== "");

    const result = {
      Customer:
        hasBuOrSlValue && appliedFilterLabelsCache.current.customers.length > 0
          ? appliedFilterLabelsCache.current.customers
          : undefined,
      Account:
        hasBuOrSlValue && appliedFilterLabelsCache.current.accounts.length > 0
          ? appliedFilterLabelsCache.current.accounts
          : undefined,
      Business_Unit: appliedFilterLabelsCache.current.businessUnit ?? undefined,
      Service_Line: appliedFilterLabelsCache.current.serviceLine ?? undefined,
      Vertical:
        hasBuOrSlValue && appliedFilterLabelsCache.current.vertical
          ? appliedFilterLabelsCache.current.vertical
          : undefined,
      Date_Range: appliedFilters.dateLabel,
    };
    return result;
  }, [appliedFilters]);

  const hasActiveFilters = React.useMemo(() => {
    return Object.values(appliedFilter).some(
      (value) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0),
    );
  }, [appliedFilter]);

  const getPrefillCustomerValues = () => ({
    customers:
      prefillValues.customer?.value !== undefined
        ? [prefillValues.customer.value]
        : [],
    accounts:
      prefillValues.customerGroup?.value !== undefined
        ? [prefillValues.customerGroup.value]
        : [],
  });

  const clearCustomerFilter = (): Partial<SimpleFilterState> => {
    const result = {
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
    };
    return result;
  };

  const clearAccountFilter = (): Partial<SimpleFilterState> => {
    const result = {
      accounts:
        prefillValues.customerGroup?.value !== undefined &&
        customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [],
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
    };
    return result;
  };

  const clearBusinessUnitFilter = (): Partial<SimpleFilterState> => {
    userManualChanges.current.add("businessUnit");
    clearCustomerOptions();
    clearVerticalOptions();
    clearCustomerGroupOptions();

    const clearedFilters = {
      businessUnit: "",
      verticals: "",
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
      accounts:
        prefillValues.customerGroup?.value !== undefined &&
        customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [],
    };
    return clearedFilters;
  };

  const clearServiceLineFilter = (): Partial<SimpleFilterState> => {
    const prefillValues_ = getPrefillCustomerValues();
    userManualChanges.current.add("serviceLine");
    if (currentFilters.businessUnit) {
      fetchCustomerGroupOptions({
        business_unit_id: appliedFilters.businessUnit ?? "",
      });
      fetchCustomerOptions({
        business_unit_id: appliedFilters.businessUnit ?? "",
        customer_group: appliedFilters.accounts
          .filter((val) => val !== EMPTY_VALUE)
          .join(","),
      });
    }

    return {
      serviceLine: "",
      ...prefillValues_,
    };
  };

  const clearVerticalFilter = (): Partial<SimpleFilterState> => {
    const prefillValues_ = getPrefillCustomerValues();
    reFetchCustomerOptions();

    return {
      verticals: "",
      ...prefillValues_,
    };
  };

  const clearDateRangeFilter = (): Partial<SimpleFilterState> => {
    const { startDate, endDate, label } = getVariantDefaultDateRange();
    return {
      dateStart: startDate,
      dateEnd: endDate,
      dateLabel: label,
    };
  };

  const applyBuSlPrefillLogic = (filters: SimpleFilterState) => {
    const bothBuSlWouldBeEmpty = !filters.businessUnit && !filters.serviceLine;
    const bothBuSlHavePrefill =
      prefillValues.businessUnit?.value !== undefined &&
      prefillValues.serviceLine?.value !== undefined;

    if ((bothBuSlWouldBeEmpty && bothBuSlHavePrefill) || bothBuSlWouldBeEmpty) {
      if (
        prefillValues.businessUnit?.value !== undefined &&
        prefillValues.businessUnit.value !== ""
      ) {
        filters.businessUnit = prefillValues.businessUnit.value;
      }
      if (
        prefillValues.serviceLine?.value !== undefined &&
        prefillValues.serviceLine.value !== ""
      ) {
        filters.serviceLine = prefillValues.serviceLine.value;
      }
    }
    return filters;
  };

  const handleClearFilter = (key: string) => {
    const filterClearActions: Record<string, () => Partial<SimpleFilterState>> =
      {
        Customer: clearCustomerFilter,
        Account: clearAccountFilter,
        Business_Unit: clearBusinessUnitFilter,
        Service_Line: clearServiceLineFilter,
        Vertical: clearVerticalFilter,
        Date_Range: clearDateRangeFilter,
      };

    const clearAction = filterClearActions[key];
    if (!clearAction) return;

    const updates = clearAction();
    const newAppliedFilters = applyBuSlPrefillLogic({
      ...appliedFilters,
      ...updates,
    });

    setCurrentFiltersWithDebug(newAppliedFilters);
    setAppliedFilters(newAppliedFilters);
    populateLabelCache(newAppliedFilters);
    setShouldApplyfilters(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setCurrentFiltersWithDebug({ ...appliedFilters });
    setIsFilterModalOpen(false);
  };

  const toggleFilterExpanded = () => {
    setIsFilterExpanded(!isFilterExpanded);
  };

  React.useEffect(() => {
    if (!isMobile && isFilterModalOpen) {
      setIsFilterModalOpen(false);
    }
  }, [isMobile, isFilterModalOpen]);

  React.useEffect(() => {
    fetchBusinessUnits();
    fetchServiceLineOptions();
  }, []);

  React.useEffect(() => {
    if (
      isPrimaryLoadingComplete() &&
      hasPrefillValues() &&
      !prefillApplied.current
    ) {
      const defaultFilters = getDefaultFilters();

      setCurrentFiltersWithDebug(defaultFilters);
      setAppliedFilters(defaultFilters);
      populateLabelCache(defaultFilters);
      setShouldApplyfilters(false);
      prefillApplied.current = true;
    }
  }, [
    loading.businessUnits,
    loading.serviceLineOptions,
    loading.verticals,
    prefillValues,
  ]);

  React.useEffect(() => {
    if (prefillApplied.current && hasPrefillValues()) {
      if (
        prefillValues.businessUnit?.value &&
        !userManualChanges.current.has("businessUnit")
      ) {
        fetchCustomerGroupOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
        });
        fetchCustomerOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          customer_group: currentFilters.accounts
            .filter((val) => val !== EMPTY_VALUE)
            .join(","),
        });
        fetchVerticalOptions(prefillValues.businessUnit.value);
      }

      if (prefillValues.vertical?.value && prefillValues.businessUnit?.value) {
        fetchCustomerGroupOptions({
          business_unit_id: prefillValues.businessUnit.value,
          vertical: prefillValues.vertical.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
        });
        fetchCustomerOptions({
          business_unit_id: prefillValues.businessUnit.value,
          vertical: prefillValues.vertical.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          customer_group: currentFilters.accounts
            .filter((val) => val !== EMPTY_VALUE)
            .join(","),
        });
      }
      if (prefillValues.businessUnit?.value) {
        fetchCustomerGroupOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
        });
        fetchCustomerOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          customer_group: currentFilters.accounts
            .filter((val) => val !== EMPTY_VALUE)
            .join(","),
        });
      }
    }
  }, [
    prefillValues.businessUnit?.value,
    prefillValues.serviceLine?.value,
    prefillValues.vertical?.value,
  ]);

  React.useEffect(() => {
    const shouldApplyPrefill =
      prefillApplied.current &&
      !userManualChanges.current.has("businessUnit") &&
      !userManualChanges.current.has("serviceLine") &&
      !userManualChanges.current.has("accounts") &&
      !userManualChanges.current.has("verticals") &&
      (prefillValues.customerGroup?.value !== undefined ||
        prefillValues.customer?.value !== undefined);
    if (shouldApplyPrefill) {
      const updatedFilters = getDefaultFilters();
      setCurrentFiltersWithDebug(updatedFilters);
      setAppliedFilters(updatedFilters);
      populateLabelCache(updatedFilters);
      setShouldApplyfilters(false);
    }
  }, [
    prefillValues.customerGroup?.value,
    prefillValues.customer?.value,
    currentFilters.businessUnit,
  ]);

  React.useEffect(() => {
    fetchCustomerGroupOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
    });
    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
    });
    fetchVerticalOptions(currentFilters.businessUnit);
  }, [currentFilters.businessUnit]);

  React.useEffect(() => {
    if (
      currentFilters.businessUnit &&
      customerGroupOptions.length > 0 &&
      prefillValues.customerGroup?.value !== undefined &&
      currentFilters.accounts.length === 0 &&
      userManualChanges.current.has("businessUnit")
    ) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        accounts: [prefillValues.customerGroup?.value ?? ""],
      }));
    }
  }, [
    customerGroupOptions.length,
    currentFilters.businessUnit,
    currentFilters.accounts.length,
  ]);

  React.useEffect(() => {
    if (
      currentFilters.businessUnit &&
      customerOptions.length > 0 &&
      prefillValues.customer?.value !== undefined &&
      currentFilters.customers.length === 0 &&
      userManualChanges.current.has("businessUnit")
    ) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        customers: [prefillValues.customer?.value ?? ""],
      }));
    }
  }, [
    customerOptions.length,
    currentFilters.businessUnit,
    currentFilters.customers.length,
  ]);

  React.useEffect(() => {
    if (
      !currentFilters.businessUnit &&
      customerGroupOptions.length > 0 &&
      prefillValues.customerGroup?.value !== undefined &&
      currentFilters.accounts.length === 0 &&
      userManualChanges.current.has("businessUnit") &&
      !userManualChanges.current.has("accounts")
    ) {
      const shouldApply = !appliedFilters.businessUnit;
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => {
        const newFilters = {
          ...prev,
          accounts: [prefillValues.customerGroup?.value ?? ""],
        };
        populateLabelCache(newFilters);
        if (shouldApply) {
          setAppliedFilters(newFilters);
          setShouldApplyfilters(false);
        }
        return newFilters;
      });
    }
  }, [
    customerGroupOptions.length,
    currentFilters.businessUnit,
    currentFilters.accounts.length,
    appliedFilters.businessUnit,
  ]);

  React.useEffect(() => {
    if (
      !currentFilters.businessUnit &&
      customerOptions.length > 0 &&
      prefillValues.customer?.value !== undefined &&
      currentFilters.customers.length === 0 &&
      userManualChanges.current.has("businessUnit") &&
      !userManualChanges.current.has("customers")
    ) {
      const shouldApply = !appliedFilters.businessUnit;
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => {
        const newFilters = {
          ...prev,
          customers: [prefillValues.customer?.value ?? ""],
        };
        populateLabelCache(newFilters);
        if (shouldApply) {
          setAppliedFilters(newFilters);
          setShouldApplyfilters(false);
        }
        return newFilters;
      });
    }
  }, [
    customerOptions.length,
    currentFilters.businessUnit,
    currentFilters.customers.length,
    appliedFilters.businessUnit,
  ]);

  React.useEffect(() => {
    if (!currentFilters.serviceLine) {
      return;
    }

    fetchCustomerGroupOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      vertical: currentFilters.verticals,
    });
    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      vertical: currentFilters.verticals,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
    });
  }, [currentFilters.serviceLine]);

  React.useEffect(() => {
    if (!currentFilters.businessUnit && !currentFilters.serviceLine) {
      return;
    }

    fetchCustomerGroupOptions({
      business_unit_id: currentFilters.businessUnit,
      vertical: currentFilters.verticals,
      service_line_id: currentFilters.serviceLine,
    });
    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      vertical: currentFilters.verticals,
      service_line_id: currentFilters.serviceLine,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
    });
  }, [currentFilters.verticals]);

  React.useEffect(() => {
    if (!currentFilters.businessUnit && !currentFilters.serviceLine) {
      return;
    }

    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      vertical: currentFilters.verticals,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
    });
  }, [currentFilters.accounts]);

  const getFilterSectionStyles = () => ({
    ...operationsDashboardStyles.filterSection,
    width: isFilterExpanded
      ? operationsDashboardStyles.filterSection.width
      : {
          xs: "auto",
          sm: "auto",
          md: "2.5rem",
          lg: "2.5rem",
          xl: "2.5rem",
        },
    minWidth: isFilterExpanded
      ? operationsDashboardStyles.filterSection.minWidth
      : {
          xs: "auto",
          sm: "auto",
          md: "2.5rem",
        },
    maxWidth: isFilterExpanded
      ? operationsDashboardStyles.filterSection.maxWidth
      : {
          xs: "100%",
          sm: "100%",
          md: "2.5rem",
        },
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease-in-out",
    padding: isFilterExpanded ? "0.75rem" : "0.75rem 0.25rem",
  });

  const keyboardTabIcon = isFilterExpanded ? (
    <KeyboardTab sx={{ transform: "rotate(180deg)" }} />
  ) : (
    <KeyboardTab color="primary" />
  );

  const isLoading = Boolean(
    (variant === "overview" && dashboardLoading) ||
    loading.businessUnits ||
    loading.serviceLineOptions,
  );

  return (
    <Box
      sx={{
        ...operationsDashboardStyles.flex1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Loading open={isLoading} />
      {!isMobile && (
        <Box sx={operationsDashboardStyles.filterContainer}>
          <ActiveFilterSection
            appliedFilter={appliedFilter as any}
            clearAllFilters={handleClearAllFilters}
            clearFilter={handleClearFilter}
            hideDateRange={true}
          />
        </Box>
      )}
      <Box
        sx={{
          ...operationsDashboardStyles.container,
          flex: 1,
          minHeight: 0,
          alignItems: "stretch",
        }}
      >
        {!isMobile && (
          <Box sx={getFilterSectionStyles()}>
            <Box
              onClick={toggleFilterExpanded}
              sx={operationsDashboardStyles.filterSectionCollapse}
            >
              {keyboardTabIcon}
            </Box>

            {isFilterExpanded && (
              <FilterComponent
                ref={filterRef}
                values={currentFilters}
                appliedValues={appliedFilters}
                userHasChanged={shouldApplyfilters}
                businessUnits={businessUnits}
                businessUnitsLoading={loading.businessUnits}
                businessUnitFilterTree={businessUnitFilterTree}
                serviceLineOptions={serviceLineOptions}
                serviceLineOptionsLoading={loading.serviceLineOptions}
                serviceLineFilterTree={serviceLineFilterTree}
                verticalOptions={verticalOptions}
                verticalOptionsLoading={loading.verticals}
                customerGroupOptions={customerGroupOptions}
                customerGroupOptionsLoading={loading.customerGroupOptions}
                customerOptions={customerOptions}
                customerOptionsLoading={loading.customerOptions}
                onAccountsChange={handleAccountsChange}
                onCustomersChange={handleCustomersChange}
                onVerticalsChange={handleVerticalsChange}
                onBusinessUnitChange={handleBusinessUnitChange}
                onServiceLineChange={handleServiceLineChange}
                onDateRangeChange={handleDateRangeChange}
                onApply={handleApplyFilters}
                onClearAll={handleClearAllFilters}
              />
            )}
          </Box>
        )}
        <Box sx={revenueAndBilledHoursStyles.contentContainer}>
          <Content
            hasActiveFilters={hasActiveFilters}
            onFilterClick={handleFilterModalOpen}
            costType={costType}
            onCostTypeChange={handleCostTypeChange}
            dateStart={dayjs(appliedFilters.dateStart)}
            dateEnd={dayjs(appliedFilters.dateEnd)}
            data={treeData}
            total={treeTotal}
            onExpandNode={handleExpandNode}
            loadingIds={loadingNodes}
          />
        </Box>
      </Box>

      <FilterModal
        open={isFilterModalOpen}
        onClose={handleFilterModalClose}
        values={currentFilters}
        appliedValues={appliedFilters}
        userHasChanged={shouldApplyfilters}
        businessUnits={businessUnits}
        businessUnitsLoading={loading.businessUnits}
        businessUnitFilterTree={businessUnitFilterTree}
        serviceLineOptions={serviceLineOptions}
        serviceLineOptionsLoading={loading.serviceLineOptions}
        serviceLineFilterTree={serviceLineFilterTree}
        verticalOptions={verticalOptions}
        verticalOptionsLoading={loading.verticals}
        customerGroupOptions={customerGroupOptions}
        customerGroupOptionsLoading={loading.customerGroupOptions}
        customerOptions={customerOptions}
        customerOptionsLoading={loading.customerOptions}
        onAccountsChange={handleAccountsChange}
        onCustomersChange={handleCustomersChange}
        onVerticalsChange={handleVerticalsChange}
        onBusinessUnitChange={handleBusinessUnitChange}
        onServiceLineChange={handleServiceLineChange}
        onDateRangeChange={handleDateRangeChange}
        onApply={handleApplyFilters}
        onClearAll={handleClearAllFilters}
      />

      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
